import { useState } from "react";
import { Sheet } from "@/components/ui/Sheet";
import { ARCHETYPES, ARCHETYPE_LABELS_AR, type Archetype } from "@/lib/constants";
import { useI18n } from "@/i18n";

// ~11 questions; each option adds weight to one archetype (PRD §5.4).
// Free (0 credits) — scored entirely client-side.
interface QItem {
  q: string;
  qAr: string;
  options: { label: string; labelAr: string; archetype: Archetype }[];
}

const QUESTIONS: QItem[] = [
  {
    q: "What feeling should your brand leave patients with?",
    qAr: "ما الشعور الذي تريد أن تتركه علامتك لدى المرضى؟",
    options: [
      { label: "Safe & cared for", labelAr: "الأمان والرعاية", archetype: "Caregiver" },
      { label: "Inspired & capable", labelAr: "الإلهام والقدرة", archetype: "Hero" },
      { label: "Wiser & informed", labelAr: "المعرفة والوعي", archetype: "Sage" },
      { label: "Transformed", labelAr: "التحوّل", archetype: "Magician" },
    ],
  },
  {
    q: "How do you talk about results?",
    qAr: "كيف تتحدث عن النتائج؟",
    options: [
      { label: "Evidence & facts", labelAr: "الأدلة والحقائق", archetype: "Sage" },
      { label: "Dramatic transformation", labelAr: "تحوّل مذهل", archetype: "Magician" },
      { label: "Beauty & confidence", labelAr: "الجمال والثقة", archetype: "Lover" },
      { label: "Bold & different", labelAr: "جريء ومختلف", archetype: "Outlaw" },
    ],
  },
  {
    q: "Your ideal tone of voice is…",
    qAr: "نبرة صوتك المثالية هي…",
    options: [
      { label: "Warm & reassuring", labelAr: "دافئة ومطمئنة", archetype: "Caregiver" },
      { label: "Authoritative", labelAr: "موثوقة وحاسمة", archetype: "Ruler" },
      { label: "Friendly & relatable", labelAr: "ودودة وقريبة", archetype: "Everyman" },
      { label: "Playful", labelAr: "مرحة", archetype: "Jester" },
    ],
  },
  {
    q: "What drives your practice most?",
    qAr: "ما الذي يقود ممارستك أكثر؟",
    options: [
      { label: "Innovation", labelAr: "الابتكار", archetype: "Creator" },
      { label: "Excellence & control", labelAr: "التميّز والإتقان", archetype: "Ruler" },
      { label: "Helping people", labelAr: "مساعدة الناس", archetype: "Caregiver" },
      { label: "Discovery", labelAr: "الاكتشاف", archetype: "Explorer" },
    ],
  },
  {
    q: "Pick a content style you love.",
    qAr: "اختر أسلوب محتوى تحبه.",
    options: [
      { label: "Educational deep-dives", labelAr: "شرح تعليمي معمّق", archetype: "Sage" },
      { label: "Before/after reveals", labelAr: "كشف قبل/بعد", archetype: "Magician" },
      { label: "Aspirational beauty", labelAr: "جمال ملهم", archetype: "Lover" },
      { label: "Relatable everyday tips", labelAr: "نصائح يومية قريبة", archetype: "Everyman" },
    ],
  },
  {
    q: "How do you want to stand out?",
    qAr: "كيف تريد أن تتميّز؟",
    options: [
      { label: "Challenge the norm", labelAr: "تحدّي السائد", archetype: "Outlaw" },
      { label: "Be the trusted leader", labelAr: "القائد الموثوق", archetype: "Ruler" },
      { label: "Be purely original", labelAr: "الأصالة الخالصة", archetype: "Creator" },
      { label: "Be adventurous", labelAr: "روح المغامرة", archetype: "Explorer" },
    ],
  },
  {
    q: "A patient's biggest win is…",
    qAr: "أكبر إنجاز للمريض هو…",
    options: [
      { label: "Feeling confident again", labelAr: "استعادة الثقة", archetype: "Lover" },
      { label: "Overcoming a struggle", labelAr: "تجاوز معاناة", archetype: "Hero" },
      { label: "Peace of mind", labelAr: "راحة البال", archetype: "Innocent" },
      { label: "Understanding their body", labelAr: "فهم أجسادهم", archetype: "Sage" },
    ],
  },
  {
    q: "Your brand's promise leans toward…",
    qAr: "وعد علامتك يميل نحو…",
    options: [
      { label: "Simplicity & purity", labelAr: "البساطة والنقاء", archetype: "Innocent" },
      { label: "Mastery & status", labelAr: "الإتقان والمكانة", archetype: "Ruler" },
      { label: "Care & nurture", labelAr: "العناية والرعاية", archetype: "Caregiver" },
      { label: "Fun & lightness", labelAr: "المرح والخفّة", archetype: "Jester" },
    ],
  },
  {
    q: "Which word fits you best?",
    qAr: "أي كلمة تناسبك أكثر؟",
    options: [
      { label: "Courageous", labelAr: "شجاع", archetype: "Hero" },
      { label: "Visionary", labelAr: "صاحب رؤية", archetype: "Magician" },
      { label: "Genuine", labelAr: "أصيل", archetype: "Everyman" },
      { label: "Curious", labelAr: "فضولي", archetype: "Explorer" },
    ],
  },
  {
    q: "Your social presence should feel…",
    qAr: "حضورك على السوشيال يجب أن يكون…",
    options: [
      { label: "Premium & refined", labelAr: "راقٍ ومميّز", archetype: "Ruler" },
      { label: "Inspiring & uplifting", labelAr: "ملهم ومحفّز", archetype: "Hero" },
      { label: "Inventive & fresh", labelAr: "مبتكر ومتجدّد", archetype: "Creator" },
      { label: "Honest & clear", labelAr: "صادق وواضح", archetype: "Sage" },
    ],
  },
  {
    q: "What change do you create?",
    qAr: "ما التغيير الذي تصنعه؟",
    options: [
      { label: "Make the impossible happen", labelAr: "تحقيق المستحيل", archetype: "Magician" },
      { label: "Restore & protect", labelAr: "الترميم والحماية", archetype: "Caregiver" },
      { label: "Empower people to win", labelAr: "تمكين الناس", archetype: "Hero" },
      { label: "Set a new standard", labelAr: "وضع معيار جديد", archetype: "Outlaw" },
    ],
  },
];

export function ArchetypeQuiz({
  open,
  onClose,
  onResult,
}: {
  open: boolean;
  onClose: () => void;
  onResult: (top3: Archetype[]) => void;
}) {
  const { lang } = useI18n();
  const [scores, setScores] = useState<Record<string, number>>({});
  const [idx, setIdx] = useState(0);

  function pick(a: Archetype) {
    const next = { ...scores, [a]: (scores[a] ?? 0) + 1 };
    setScores(next);
    if (idx < QUESTIONS.length - 1) {
      setIdx(idx + 1);
    } else {
      const top3 = [...ARCHETYPES]
        .sort((x, y) => (next[y] ?? 0) - (next[x] ?? 0))
        .slice(0, 3);
      onResult(top3);
      setScores({});
      setIdx(0);
      onClose();
    }
  }

  const q = QUESTIONS[idx];

  return (
    <Sheet open={open} onClose={onClose} title={`${idx + 1} / ${QUESTIONS.length}`}>
      <p className="mb-4 text-base font-medium">{lang === "ar" ? q.qAr : q.q}</p>
      <div className="grid gap-2">
        {q.options.map((o) => (
          <button
            key={o.archetype + o.label}
            className="btn-ghost justify-start text-start"
            onClick={() => pick(o.archetype)}
          >
            {lang === "ar"
              ? `${o.labelAr} — ${ARCHETYPE_LABELS_AR[o.archetype]}`
              : o.label}
          </button>
        ))}
      </div>
    </Sheet>
  );
}
